import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { placesAutocomplete, placesDetails } from '@/lib/google-places';

interface AddressComponents {
  streetAddress: string;
  city: string;
  stateProvince: string;
  zipPostalCode: string;
  country: 'CA' | 'US' | 'FR' | 'UK' | 'OTHER' | '';
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (components: AddressComponents) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  className?: string;
}

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

// Country code mapping
const countryMapping: Record<string, AddressComponents['country']> = {
  'CA': 'CA',
  'US': 'US',
  'FR': 'FR',
  'GB': 'UK',
  'UK': 'UK',
};

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Start typing your address...',
  error = false,
  disabled = false,
  className = '',
}: AddressAutocompleteProps) {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch autocomplete predictions from edge function
  const fetchPredictions = useCallback(async (input: string) => {
    if (input.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await placesAutocomplete({ input, types: 'address', language: 'en' });

      if (result.predictions && result.predictions.length > 0) {
        setPredictions(result.predictions);
        setShowDropdown(true);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    } catch (err) {
      console.error('Error fetching address predictions:', err);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced input handler
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 300);
  }, [onChange, fetchPredictions]);

  // Fetch place details and parse address components
  const handleSelectPlace = useCallback(async (prediction: Prediction) => {
    setIsLoading(true);
    setShowDropdown(false);
    onChange(prediction.description);

    try {
      const data = await placesDetails({ placeId: prediction.place_id, language: 'en' });

      if (data.result && data.result.address_components) {
        const components = data.result.address_components;
        
        let streetNumber = '';
        let route = '';
        let city = '';
        let stateProvince = '';
        let zipPostalCode = '';
        let countryCode = '';

        components.forEach((component: { types: string[]; long_name: string; short_name: string }) => {
          const types = component.types;
          
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (types.includes('route')) {
            route = component.long_name;
          }
          if (types.includes('locality') || types.includes('sublocality')) {
            city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            stateProvince = component.short_name;
          }
          if (types.includes('postal_code')) {
            zipPostalCode = component.long_name;
          }
          if (types.includes('country')) {
            countryCode = component.short_name;
          }
        });

        const streetAddress = streetNumber && route 
          ? `${streetNumber} ${route}` 
          : route || data.result.formatted_address?.split(',')[0] || '';

        const country = countryMapping[countryCode] || 'OTHER';

        onAddressSelect({
          streetAddress,
          city,
          stateProvince,
          zipPostalCode,
          country,
        });
      }
    } catch (err) {
      console.error('Error fetching place details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [onChange, onAddressSelect]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, predictions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handleSelectPlace(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showDropdown, predictions, selectedIndex, handleSelectPlace]);

  // Handle clear button
  const handleClear = useCallback(() => {
    onChange('');
    setPredictions([]);
    setShowDropdown(false);
    onAddressSelect({
      streetAddress: '',
      city: '',
      stateProvince: '',
      zipPostalCode: '',
      country: '',
    });
    inputRef.current?.focus();
  }, [onChange, onAddressSelect]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const baseClasses =
    'w-full pl-10 pr-10 py-2.5 rounded-lg bg-white border text-[#1D314D] placeholder:text-slate-400 focus:ring-2 focus:ring-[#CEAB65]/50 focus:border-[#CEAB65] transition duration-150';
  const errorClasses = error ? 'border-red-500' : 'border-slate-300';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : '';

  return (
    <div className="relative">
      {/* Search Icon */}
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
        <Search className="w-4 h-4" />
      </div>

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => predictions.length > 0 && setShowDropdown(true)}
        placeholder={placeholder}
        disabled={disabled}
        className={`${baseClasses} ${errorClasses} ${disabledClasses} ${className}`}
        autoComplete="off"
      />

      {/* Loading/Clear Button */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {isLoading ? (
          <Loader2 className="w-4 h-4 text-[#CEAB65] animate-spin" />
        ) : value ? (
          <button
            type="button"
            onClick={handleClear}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            tabIndex={-1}
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>

      {/* Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelectPlace(prediction)}
              className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-slate-100' : ''
              }`}
            >
              <div className="text-sm font-medium text-[#1D314D]">
                {prediction.structured_formatting?.main_text || prediction.description.split(',')[0]}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {prediction.structured_formatting?.secondary_text || 
                  prediction.description.split(',').slice(1).join(',').trim()}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
